import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Chip,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Checkbox,
  Switch,
  FormControlLabel,
  Divider
} from '@mui/material';
import {
  Search,
  Refresh,
  FilterList,
  Clear,
  CheckCircle,
  Cancel,
  TrendingUp,
  TrendingDown,
  School,
  AssignmentTurnedIn,
  People,
  Gavel,
  ArrowForward,
  Undo
} from '@mui/icons-material';

const Passation = () => {
  const [etudiants, setEtudiants] = useState([]);
  const [inscriptions, setInscriptions] = useState([]);
  const [allFilieres, setAllFilieres] = useState([]);
  const [allAnnees, setAllAnnees] = useState([]);
  const [allNiveaux, setAllNiveaux] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [messageSeverity, setMessageSeverity] = useState('success');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // États pour les filtres cascadants (affichage)
  const [showFiltres, setShowFiltres] = useState(true);
  const [filtreAnnee, setFiltreAnnee] = useState('all');
  const [filtreFiliere, setFiltreFiliere] = useState('all');
  const [filtreNiveau, setFiltreNiveau] = useState('all');
  
  // États pour les listes filtrées (cascading) - Affichage
  const [filieresFiltrees, setFilieresFiltrees] = useState([]);
  const [niveauxFiltres, setNiveauxFiltres] = useState([]);
  
  // États pour le formulaire de passation
  const [formulairePassation, setFormulairePassation] = useState({
    annee_destination: '',
    filiere_destination: '',
    niveau_destination: ''
  });
  
  // États pour les listes filtrées du formulaire
  const [filieresDestination, setFilieresDestination] = useState([]);
  const [niveauxDestination, setNiveauxDestination] = useState([]);
  
  // Mode passation forcée (sans note)
  const [modeForcé, setModeForcé] = useState(false);
  
  // Afficher aussi les étudiants déjà passés
  const [showPasses, setShowPasses] = useState(false);
  
  // États pour la sélection des étudiants
  const [selectedEtudiants, setSelectedEtudiants] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [passationDialogOpen, setPassationDialogOpen] = useState(false);
  const [passationLoading, setPassationLoading] = useState(false);
  
  // États pour les statistiques
  const [stats, setStats] = useState({
    total: 0,
    eligibles: 0,
    nonEligibles: 0,
    sansNotes: 0,
    moyenneGenerale: 0,
    selectedCount: 0
  });

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  useEffect(() => {
    loadData();
  }, []);

  // ========== CHARGER LES FILIÈRES PAR ANNÉE ==========
  const loadFilieresByAnnee = async (anneeId) => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`/api/filieres/by-annee/${anneeId}`, { headers });
      return res.data || [];
    } catch (err) {
      console.error('Erreur chargement filières:', err);
      return [];
    }
  };

  // ========== CHARGER LES NIVEAUX PAR FILIÈRE ==========
  const loadNiveauxByFiliere = async (filiereId) => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`/api/niveaux/by-filiere/${filiereId}`, { headers });
      return res.data || [];
    } catch (err) {
      console.error('Erreur chargement niveaux:', err);
      return [];
    }
  };

  // ========== CHARGER LES DONNÉES ==========
  const loadData = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();

      const [etudiantsRes, inscriptionsRes, filieresRes, anneesRes, niveauxRes, notesRes] = await Promise.all([
        axios.get('/api/etudiants', { params: { limit: 9999 }, headers }),
        axios.get('/api/inscriptions', { headers }),
        axios.get('/api/filieres', { headers }),
        axios.get('/api/annees', { headers }),
        axios.get('/api/niveaux', { headers }).catch(() => ({ data: [] })),
        axios.get('/api/notes', { headers }).catch(() => ({ data: [] }))
      ]);
      
      console.log('📊 Données chargées - Passation:', {
        etudiants: etudiantsRes.data?.length || 0,
        inscriptions: inscriptionsRes.data?.length || 0,
        filieres: filieresRes.data?.length || 0,
        annees: anneesRes.data?.length || 0,
        niveaux: niveauxRes.data?.length || 0,
        notes: notesRes.data?.length || 0
      });
      
      setEtudiants(etudiantsRes.data?.data || etudiantsRes.data || []);
      setInscriptions(inscriptionsRes.data?.data || inscriptionsRes.data || []);
      setAllFilieres(filieresRes.data?.data || filieresRes.data || []);
      setAllAnnees(anneesRes.data?.data || anneesRes.data || []);
      setAllNiveaux(niveauxRes.data?.data || niveauxRes.data || []);
      setNotes(notesRes.data?.data || notesRes.data || []);
      setFilieresFiltrees(filieresRes.data || []);
      setNiveauxFiltres(niveauxRes.data || []);
      setSelectedEtudiants([]);
      setSelectAll(false);
      setError('');
    } catch (err) {
      console.error('Erreur:', err);
      setError('Impossible de charger les données');
    } finally {
      setLoading(false);
    }
  };

  // ========== EFFET : MISE À JOUR DES FILIÈRES QUAND L'ANNÉE CHANGE ==========
  useEffect(() => {
    const updateFilieres = async () => {
      if (filtreAnnee === 'all') {
        setFilieresFiltrees(allFilieres);
        setNiveauxFiltres(allNiveaux);
      } else {
        const filieres = await loadFilieresByAnnee(filtreAnnee);
        setFilieresFiltrees(filieres);
        setFiltreFiliere('all');
        setFiltreNiveau('all');
        setNiveauxFiltres(allNiveaux);
      }
    };
    
    updateFilieres();
  }, [filtreAnnee, allFilieres]);

  // ========== EFFET : MISE À JOUR DES NIVEAUX QUAND LA FILIÈRE CHANGE ==========
  useEffect(() => {
    const updateNiveaux = async () => {
      if (filtreFiliere !== 'all') {
        const niveaux = await loadNiveauxByFiliere(filtreFiliere);
        setNiveauxFiltres(niveaux);
      } else {
        setNiveauxFiltres(allNiveaux);
      }
    };
    
    updateNiveaux();
  }, [filtreFiliere, allNiveaux]);

  // ========== EFFET : MISE À JOUR DES FILIÈRES DESTINATION ==========
  useEffect(() => {
    const updateFilieresDestination = async () => {
      if (formulairePassation.annee_destination) {
        const filieres = await loadFilieresByAnnee(formulairePassation.annee_destination);
        setFilieresDestination(filieres);
        setFormulairePassation(prev => ({ ...prev, filiere_destination: '' }));
        setNiveauxDestination([]);
      } else {
        setFilieresDestination([]);
        setNiveauxDestination([]);
      }
    };
    
    updateFilieresDestination();
  }, [formulairePassation.annee_destination]);

  // ========== EFFET : MISE À JOUR DES NIVEAUX DESTINATION ==========
  useEffect(() => {
    const updateNiveauxDestination = async () => {
      if (formulairePassation.filiere_destination) {
        const niveaux = await loadNiveauxByFiliere(formulairePassation.filiere_destination);
        setNiveauxDestination(niveaux);
        setFormulairePassation(prev => ({ ...prev, niveau_destination: '' }));
      } else {
        setNiveauxDestination([]);
      }
    };
    
    updateNiveauxDestination();
  }, [formulairePassation.filiere_destination]);

  // ========== FONCTIONS UTILITAIRES AVEC NOUVELLE STRUCTURE ==========
  
  // Rechercher un étudiant par son ID (compatible avec les deux formats)
  const getEtudiantById = (etudiantId) => {
    if (!etudiantId) return null;
    const searchId = String(etudiantId);
    
    // Recherche par _id (ObjectId)
    let found = etudiants.find(e => String(e._id) === searchId);
    if (found) return found;
    
    // Recherche par etudiant_id (ancien format)
    found = etudiants.find(e => String(e.etudiant_id) === searchId);
    if (found) return found;
    
    // Recherche par id (champ id)
    found = etudiants.find(e => String(e.id) === searchId);
    if (found) return found;
    
    // Recherche par CNE
    found = etudiants.find(e => String(e.CNE) === searchId);
    if (found) return found;
    
    return null;
  };

  // Récupérer l'inscription active d'un étudiant
  const getInscriptionActive = (etudiantId) => {
    if (!etudiantId) return null;
    const searchId = String(etudiantId);
    
    const inscriptionsEtudiant = inscriptions.filter(ins => {
      const insEtudiantId = String(ins.etudiant_id);
      return insEtudiantId === searchId || 
             insEtudiantId === String(etudiantId) ||
             String(ins.etudiant) === searchId;
    });
    
    if (inscriptionsEtudiant.length === 0) return null;
    
    return inscriptionsEtudiant.sort((a, b) => 
      new Date(b.date_inscription || b.createdAt || b.dateCreation) - 
      new Date(a.date_inscription || a.createdAt || a.dateCreation)
    )[0];
  };

  // Calculer la moyenne d'un étudiant
  const calculerMoyenne = (etudiantId) => {
    if (!etudiantId) return null;
    const searchId = String(etudiantId);
    
    const notesEtudiant = notes.filter(n => {
      const noteEtudiantId = String(n.etudiant_id);
      return noteEtudiantId === searchId || noteEtudiantId === String(etudiantId);
    });
    
    if (notesEtudiant.length === 0) return null;
    const total = notesEtudiant.reduce((sum, n) => sum + (n.valeur || n.note || 0), 0);
    return total / notesEtudiant.length;
  };

  // Récupérer la filière par ID
  const getFiliere = (filiereId) => {
    if (!filiereId) return null;
    const searchId = String(filiereId);
    return allFilieres.find(f => 
      String(f.filiere_id) === searchId || 
      String(f._id) === searchId ||
      String(f.id) === searchId
    );
  };

  // Récupérer l'année par ID
  const getAnnee = (anneeId) => {
    if (!anneeId) return null;
    const searchId = String(anneeId);
    return allAnnees.find(a => 
      String(a.annee_id) === searchId || 
      String(a._id) === searchId ||
      String(a.id) === searchId
    );
  };

  // Récupérer le niveau par ID
  const getNiveau = (niveauId) => {
    if (!niveauId) return null;
    const searchId = String(niveauId);
    return allNiveaux.find(n => 
      String(n.niveau_id) === searchId || 
      String(n._id) === searchId ||
      String(n.id) === searchId
    );
  };

  // ========== ENRICHIR LES ÉTUDIANTS AVEC NOUVELLE STRUCTURE ==========
  const enrichedEtudiants = etudiants.map(etudiant => {
    const etudiantId = etudiant._id || etudiant.etudiant_id || etudiant.id;
    const inscription = getInscriptionActive(etudiantId);
    const moyenne = calculerMoyenne(etudiantId);
    
    // Vérifier l'éligibilité
    const estEligible = modeForcé ? true : (moyenne !== null && moyenne >= 10);
    
    // Récupérer les informations de la filière, année et niveau
    const filiere = getFiliere(inscription?.filiere_id);
    const annee = getAnnee(inscription?.annee_universitaire_id);
    const niveau = getNiveau(inscription?.niveau_id);
    
    // Normaliser les champs de l'étudiant
    const normalizedEtudiant = {
      ...etudiant,
      etudiant_cne_masar: etudiant.CNE || etudiant.etudiant_cne_masar || '-',
      etudiant_nom: etudiant.Nom || etudiant.etudiant_nom || '-',
      etudiant_prenom: etudiant.Prénom || etudiant.etudiant_prenom || '-',
      etudiant_email: etudiant.email || etudiant.etudiant_email || '-',
      etudiant_tel: etudiant.Téléphone || etudiant.etudiant_tel || '-'
    };
    
    return {
      ...normalizedEtudiant,
      inscription,
      filiereInfo: filiere,
      anneeInfo: annee,
      niveauInfo: niveau,
      moyenne: moyenne,
      aDesNotes: moyenne !== null,
      estEligible: estEligible,
      statutInscription: inscription?.statut || 'NON_INSCRIT',
      estPasse: inscription?.estPasse || false
    };
  });

  // ========== FILTRER LES ÉTUDIANTS ==========
  const filteredEtudiants = enrichedEtudiants.filter(e => {
    const search = searchTerm.toLowerCase();
    const matchesSearch = 
      (e.etudiant_nom || '').toLowerCase().includes(search) ||
      (e.etudiant_prenom || '').toLowerCase().includes(search) ||
      (e.etudiant_cne_masar || '').toLowerCase().includes(search);
    
    const matchesAnnee = filtreAnnee === 'all' || 
      (e.anneeInfo && String(e.anneeInfo.annee_id || e.anneeInfo._id) === filtreAnnee);
    
    const matchesFiliere = filtreFiliere === 'all' || 
      (e.filiereInfo && String(e.filiereInfo.filiere_id || e.filiereInfo._id) === filtreFiliere);
    
    const matchesNiveau = filtreNiveau === 'all' || 
      (e.niveauInfo && String(e.niveauInfo.niveau_id || e.niveauInfo._id) === filtreNiveau);
    
    // Ne montrer que les étudiants avec une inscription confirmée ou en attente, et non passés
    const isInscrit = e.statutInscription === 'CONFIRMEE' || e.statutInscription === 'EN_ATTENTE';
    
    return matchesSearch && matchesAnnee && matchesFiliere && matchesNiveau && 
           isInscrit && (!e.estPasse || showPasses);
  });

  // ========== STATISTIQUES ==========
  useEffect(() => {
    const total = filteredEtudiants.length;
    const eligibles = filteredEtudiants.filter(e => e.estEligible === true).length;
    const nonEligibles = filteredEtudiants.filter(e => e.estEligible === false).length;
    const sansNotes = filteredEtudiants.filter(e => !e.aDesNotes).length;
    const moyennes = filteredEtudiants.map(e => e.moyenne).filter(m => m !== null);
    const moyenneGenerale = moyennes.length > 0 
      ? moyennes.reduce((a, b) => a + b, 0) / moyennes.length 
      : 0;
    
    setStats({ 
      total, 
      eligibles, 
      nonEligibles, 
      sansNotes,
      moyenneGenerale, 
      selectedCount: selectedEtudiants.length 
    });
  }, [filteredEtudiants, selectedEtudiants]);

  // ========== GESTION DE LA SÉLECTION ==========
  const handleSelectAll = (event) => {
    const checked = event.target.checked;
    setSelectAll(checked);
    if (checked) {
      const ids = filteredEtudiants
        .filter(e => e.estEligible === true)
        .map(e => e._id || e.etudiant_id);
      setSelectedEtudiants(ids);
    } else {
      setSelectedEtudiants([]);
    }
  };

  const handleSelectOne = (event, id) => {
    const checked = event.target.checked;
    setSelectedEtudiants(prev => {
      if (checked) {
        return [...prev, id];
      } else {
        return prev.filter(item => item !== id);
      }
    });
    setSelectAll(false);
  };

  // ========== EFFECTUER LA PASSATION ==========
  const handlePassation = async () => {
    if (selectedEtudiants.length === 0) {
      setMessage('Veuillez sélectionner au moins un étudiant', 'warning');
      return;
    }
    
    if (!formulairePassation.annee_destination) {
      setMessage('Veuillez sélectionner une année de destination', 'warning');
      return;
    }
    
    if (!formulairePassation.filiere_destination) {
      setMessage('Veuillez sélectionner une filière de destination', 'warning');
      return;
    }
    
    if (!formulairePassation.niveau_destination) {
      setMessage('Veuillez sélectionner un niveau de destination', 'warning');
      return;
    }
    
    setPassationDialogOpen(true);
  };

  const confirmPassation = async () => {
    setPassationLoading(true);
    try {
      const headers = getAuthHeaders();
      
      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      
      for (const etudiantId of selectedEtudiants) {
        try {
          // Trouver l'inscription de l'étudiant
          const etudiant = getEtudiantById(etudiantId);
          if (!etudiant) {
            errorCount++;
            errors.push(`Étudiant ${etudiantId} non trouvé`);
            continue;
          }
          
          const inscription = getInscriptionActive(etudiantId);
          if (!inscription) {
            errorCount++;
            errors.push(`Aucune inscription pour l'étudiant ${etudiant.etudiant_nom} ${etudiant.etudiant_prenom}`);
            continue;
          }
          
          const id = inscription._id || inscription.id;
          
          // Effectuer la passation
          await axios.put(
            `/api/inscriptions/${id}/passation`,
            {
              force: modeForcé,
              annee_destination: formulairePassation.annee_destination,
              filiere_destination: formulairePassation.filiere_destination,
              niveau_destination: formulairePassation.niveau_destination
            },
            { headers }
          );
          successCount++;
        } catch (err) {
          console.error(`❌ Erreur pour l'étudiant ${etudiantId}:`, err);
          errorCount++;
          errors.push(err.response?.data?.message || err.response?.data?.error || err.message);
        }
      }
      
      let msg = `✅ ${successCount} étudiant(s) ont été passés avec succès`;
      if (errorCount > 0) {
        msg += `, ${errorCount} erreur(s)`;
        if (errors.length > 0) {
          msg += `: ${errors.slice(0, 3).join(', ')}${errors.length > 3 ? '...' : ''}`;
        }
      }
      setMessage(msg, successCount > 0 ? 'success' : 'error');
      await loadData();
      setPassationDialogOpen(false);
      setSelectedEtudiants([]);
      setSelectAll(false);
      setFormulairePassation({
        annee_destination: '',
        filiere_destination: '',
        niveau_destination: ''
      });
      setFilieresDestination([]);
      setNiveauxDestination([]);
    } catch (err) {
      console.error('Erreur passation:', err);
      setMessage('❌ Erreur lors de la passation: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setPassationLoading(false);
    }
  };

  const handleToggleEstPasse = async (etudiant) => {
    const inscription = etudiant.inscription;
    if (!inscription) return;
    const id = inscription._id || inscription.id;
    if (!id) return;
    const newValue = !inscription.estPasse;
    try {
      await axios.put(`/api/inscriptions/${id}/est-passe`, { estPasse: newValue }, { headers: getAuthHeaders() });
      setMessage(newValue ? '✅ Étudiant marqué comme passé' : '🔄 Passation réinitialisée', 'success');
      setPassationLoading(false);
      await loadData();
    } catch (err) {
      console.error('❌ Erreur est-passe:', err);
      setMessage('❌ Erreur: ' + (err.response?.data?.message || err.message), 'error');
    }
  };

  const resetFilters = () => {
    setFiltreAnnee('all');
    setFiltreFiliere('all');
    setFiltreNiveau('all');
    setSearchTerm('');
    setFilieresFiltrees(allFilieres);
    setNiveauxFiltres(allNiveaux);
    setPage(0);
  };

  const resetFormulairePassation = () => {
    setFormulairePassation({
      annee_destination: '',
      filiere_destination: '',
      niveau_destination: ''
    });
    setFilieresDestination([]);
    setNiveauxDestination([]);
  };

  const showMessage = (msg, severity = 'success') => {
    setMessage(msg);
    setMessageSeverity(severity);
    setTimeout(() => setMessage(''), 4000);
  };

  const activeFiltersCount = [
    filtreAnnee !== 'all',
    filtreFiliere !== 'all',
    filtreNiveau !== 'all',
    searchTerm !== ''
  ].filter(Boolean).length;

  // Pagination
  const paginatedEtudiants = filteredEtudiants.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Messages */}
      <Snackbar
        open={!!message}
        autoHideDuration={4000}
        onClose={() => setMessage('')}
        message={message}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />

      {/* En-tête */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ color: '#1f2937' }}>
            🔄 Passation
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {stats.total} étudiant(s) - {stats.eligibles} éligible(s) - {stats.selectedCount} sélectionné(s)
            {stats.sansNotes > 0 && ` - ${stats.sansNotes} sans note`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <FormControlLabel
            control={
              <Switch
                checked={modeForcé}
                onChange={(e) => {
                  setModeForcé(e.target.checked);
                  setSelectedEtudiants([]);
                  setSelectAll(false);
                }}
                color="warning"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Gavel fontSize="small" color={modeForcé ? 'warning' : 'disabled'} />
                <Typography variant="body2" color={modeForcé ? 'warning.main' : 'text.secondary'}>
                  Mode forcé
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={showPasses}
                onChange={(e) => {
                  setShowPasses(e.target.checked);
                  setSelectedEtudiants([]);
                  setSelectAll(false);
                }}
                color="primary"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CheckCircle fontSize="small" color={showPasses ? 'primary' : 'disabled'} />
                <Typography variant="body2" color={showPasses ? 'primary.main' : 'text.secondary'}>
                  Afficher les passés
                </Typography>
              </Box>
            }
          />
          <Button
            variant={showFiltres ? 'contained' : 'outlined'}
            startIcon={<FilterList />}
            onClick={() => setShowFiltres(!showFiltres)}
            size="medium"
          >
            {showFiltres ? 'Masquer filtres' : 'Afficher filtres'}
            {activeFiltersCount > 0 && ` (${activeFiltersCount})`}
          </Button>
          {activeFiltersCount > 0 && (
            <Button
              variant="text"
              startIcon={<Clear />}
              onClick={resetFilters}
              size="medium"
              color="error"
            >
              Réinitialiser
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadData}
          >
            Actualiser
          </Button>
          <Button
            variant="contained"
            startIcon={<AssignmentTurnedIn />}
            onClick={handlePassation}
            disabled={selectedEtudiants.length === 0}
            sx={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
              },
              '&:disabled': {
                background: '#9ca3af'
              }
            }}
          >
            Effectuer la passation ({selectedEtudiants.length})
          </Button>
        </Stack>
      </Box>

      {/* Statistiques */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <Card sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>📊 Total</Typography>
              <Typography variant="h4" fontWeight="bold">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white' }}>
            <CardContent>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {modeForcé ? '🔓 Éligibles (forcé)' : '✅ Éligibles'}
              </Typography>
              <Typography variant="h4" fontWeight="bold">{stats.eligibles}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white' }}>
            <CardContent>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>📈 Moyenne</Typography>
              <Typography variant="h4" fontWeight="bold">{stats.moyenneGenerale.toFixed(1)}/20</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', color: 'white' }}>
            <CardContent>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>⏳ En attente</Typography>
              <Typography variant="h4" fontWeight="bold">{stats.total - stats.eligibles}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Formulaire de passation */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, border: '2px solid #667eea' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <ArrowForward sx={{ color: '#667eea' }} />
          <Typography variant="h6" fontWeight="bold" sx={{ color: '#1f2937' }}>
            📋 Paramètres de la passation
          </Typography>
          <Chip 
            label={modeForcé ? 'Mode forcé activé' : 'Mode normal'} 
            size="small" 
            color={modeForcé ? 'warning' : 'success'} 
            sx={{ ml: 1 }}
          />
        </Box>
        <Divider sx={{ mb: 2 }} />
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth required>
              <InputLabel>📅 Année de destination *</InputLabel>
              <Select
                value={formulairePassation.annee_destination}
                onChange={(e) => {
                  setFormulairePassation(prev => ({ 
                    ...prev, 
                    annee_destination: e.target.value,
                    filiere_destination: '',
                    niveau_destination: ''
                  }));
                  setNiveauxDestination([]);
                }}
                label="📅 Année de destination *"
              >
                <MenuItem value="">Sélectionner une année</MenuItem>
                {allAnnees.map((a) => (
                  <MenuItem key={a.annee_id || a._id} value={a.annee_id || a._id}>
                    {a.intitule}
                    {a.estActive && (
                      <Chip label="Active" size="small" color="success" sx={{ ml: 1 }} />
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth required disabled={!formulairePassation.annee_destination}>
              <InputLabel>🎓 Filière de destination *</InputLabel>
              <Select
                value={formulairePassation.filiere_destination}
                onChange={(e) => {
                  setFormulairePassation(prev => ({ 
                    ...prev, 
                    filiere_destination: e.target.value,
                    niveau_destination: ''
                  }));
                }}
                label="🎓 Filière de destination *"
              >
                <MenuItem value="">
                  {!formulairePassation.annee_destination 
                    ? 'Sélectionnez d\'abord une année' 
                    : 'Sélectionner une filière'}
                </MenuItem>
                {filieresDestination.map((f) => (
                  <MenuItem key={f.filiere_id || f._id} value={f.filiere_id || f._id}>
                    {f.libelle}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {formulairePassation.annee_destination && filieresDestination.length === 0 && (
              <Typography variant="caption" color="warning.main">
                ⚠️ Aucune filière trouvée pour cette année
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth required disabled={!formulairePassation.filiere_destination}>
              <InputLabel>📚 Niveau de destination *</InputLabel>
              <Select
                value={formulairePassation.niveau_destination}
                onChange={(e) => {
                  setFormulairePassation(prev => ({ 
                    ...prev, 
                    niveau_destination: e.target.value 
                  }));
                }}
                label="📚 Niveau de destination *"
              >
                <MenuItem value="">
                  {!formulairePassation.filiere_destination 
                    ? 'Sélectionnez d\'abord une filière' 
                    : 'Sélectionner un niveau'}
                </MenuItem>
                {niveauxDestination.map((n) => (
                  <MenuItem key={n.niveau_id || n._id} value={n.niveau_id || n._id}>
                    {n.intitule}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {formulairePassation.filiere_destination && niveauxDestination.length === 0 && (
              <Typography variant="caption" color="warning.main">
                ⚠️ Aucun niveau trouvé pour cette filière
              </Typography>
            )}
          </Grid>
        </Grid>

        {/* Récapitulatif du formulaire */}
        {formulairePassation.annee_destination && formulairePassation.filiere_destination && formulairePassation.niveau_destination && (
          <Box sx={{ mt: 2, p: 2, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid #bbf7d0' }}>
            <Typography variant="subtitle2" fontWeight="bold" color="#166534">
              ✅ Passation vers :
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
              <Chip 
                label={`Année: ${allAnnees.find(a => String(a.annee_id || a._id) === formulairePassation.annee_destination)?.intitule || 'N/A'}`}
                size="small"
                variant="outlined"
                color="primary"
              />
              <Chip 
                label={`Filière: ${allFilieres.find(f => String(f.filiere_id || f._id) === formulairePassation.filiere_destination)?.libelle || 'N/A'}`}
                size="small"
                variant="outlined"
                color="secondary"
              />
              <Chip 
                label={`Niveau: ${niveauxDestination.find(n => String(n.niveau_id || n._id) === formulairePassation.niveau_destination)?.intitule || 'N/A'}`}
                size="small"
                variant="outlined"
                color="success"
              />
            </Stack>
          </Box>
        )}
        
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button 
            variant="text" 
            size="small" 
            onClick={resetFormulairePassation}
            startIcon={<Clear />}
          >
            Réinitialiser
          </Button>
        </Box>
      </Paper>

      {/* Filtres Cascadants (Affichage) */}
      {showFiltres && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
            🔍 Filtrer les étudiants
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>📅 Année</InputLabel>
                <Select
                  value={filtreAnnee}
                  onChange={(e) => {
                    setFiltreAnnee(e.target.value);
                    setFiltreFiliere('all');
                    setFiltreNiveau('all');
                    setPage(0);
                  }}
                  label="📅 Année"
                >
                  <MenuItem value="all">Toutes les années</MenuItem>
                  {allAnnees.map((a) => (
                    <MenuItem key={a.annee_id || a._id} value={a.annee_id || a._id}>
                      {a.intitule}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>🎓 Filière</InputLabel>
                <Select
                  value={filtreFiliere}
                  onChange={(e) => {
                    setFiltreFiliere(e.target.value);
                    setFiltreNiveau('all');
                    setPage(0);
                  }}
                  label="🎓 Filière"
                  disabled={!filtreAnnee || filtreAnnee === 'all'}
                >
                  <MenuItem value="all">
                    {filtreAnnee === 'all' || !filtreAnnee 
                      ? 'Toutes les filières' 
                      : 'Toutes les filières de l\'année'}
                  </MenuItem>
                  {filieresFiltrees.map((f) => (
                    <MenuItem key={f.filiere_id || f._id} value={f.filiere_id || f._id}>
                      {f.libelle}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>📚 Niveau</InputLabel>
                <Select
                  value={filtreNiveau}
                  onChange={(e) => {
                    setFiltreNiveau(e.target.value);
                    setPage(0);
                  }}
                  label="📚 Niveau"
                  disabled={!filtreFiliere || filtreFiliere === 'all'}
                >
                  <MenuItem value="all">
                    {filtreFiliere === 'all' || !filtreFiliere
                      ? 'Tous les niveaux'
                      : 'Tous les niveaux de la filière'}
                  </MenuItem>
                  {niveauxFiltres.map((n) => (
                    <MenuItem key={n.niveau_id || n._id} value={n.niveau_id || n._id}>
                      {n.intitule}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Recherche */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <TextField
          fullWidth
          placeholder="Rechercher par nom, prénom ou CNE..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Paper>

      {/* Tableau */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ background: '#f8fafc' }}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectAll && filteredEtudiants.filter(e => e.estEligible).length > 0}
                  onChange={handleSelectAll}
                  disabled={filteredEtudiants.filter(e => e.estEligible).length === 0}
                />
              </TableCell>
              
              <TableCell sx={{ fontWeight: 'bold' }}>Étudiant</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Filière</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Niveau</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Année</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Moyenne</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Passé</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedEtudiants.map((etudiant) => {
              const moyenne = etudiant.moyenne;
              const estEligible = etudiant.estEligible;
              const aDesNotes = etudiant.aDesNotes;
              const moyenneAffichee = moyenne !== null ? moyenne.toFixed(1) : 'Sans note';
              const isChecked = selectedEtudiants.includes(etudiant._id || etudiant.etudiant_id);
              
              return (
                <TableRow key={etudiant._id || etudiant.etudiant_id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isChecked}
                      onChange={(e) => handleSelectOne(e, etudiant._id || etudiant.etudiant_id)}
                      disabled={!estEligible}
                    />
                  </TableCell>
                  
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: '#667eea' }}>
                        {(etudiant.etudiant_prenom || '?')[0]?.toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {etudiant.etudiant_nom || '-'} {etudiant.etudiant_prenom || '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={etudiant.filiereInfo?.libelle}
                      size="small"
                      sx={{ bgcolor: '#e0e7ff', color: '#4338ca' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={etudiant.niveauInfo?.intitule || '-'}
                      size="small"
                      sx={{ bgcolor: '#f3e8ff', color: '#7c3aed' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={etudiant.anneeInfo?.intitule || '-'}
                      size="small"
                      sx={{ bgcolor: '#dbeafe', color: '#1e40af' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography 
                        variant="body2" 
                        fontWeight={600}
                        color={aDesNotes ? (estEligible ? '#10b981' : '#ef4444') : '#f59e0b'}
                      >
                        {moyenneAffichee}
                        {!aDesNotes && ' ⚠️'}
                      </Typography>
                      {aDesNotes && (
                        <LinearProgress
                          variant="determinate"
                          value={moyenne !== null ? (moyenne / 20) * 100 : 0}
                          sx={{ 
                            width: 50, 
                            height: 6, 
                            borderRadius: 3,
                            bgcolor: '#f3f4f6',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: estEligible ? '#10b981' : '#ef4444'
                            }
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {modeForcé ? (
                      <Chip
                        icon={<Gavel />}
                        label="Éligible (forcé)"
                        size="small"
                        sx={{ bgcolor: '#fef3c7', color: '#92400e' }}
                      />
                    ) : estEligible ? (
                      <Chip
                        icon={<TrendingUp />}
                        label={aDesNotes ? "Éligible" : "Éligible"}
                        size="small"
                        sx={{ bgcolor: '#dcfce7', color: '#166534' }}
                      />
                    ) : (
                      <Chip
                        icon={<TrendingDown />}
                        label={!aDesNotes ? "Sans note" : "Non éligible"}
                        size="small"
                        sx={{ bgcolor: '#fee2e2', color: '#991b1b' }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {etudiant.estPasse ? (
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<Undo />}
                        onClick={() => handleToggleEstPasse(etudiant)}
                      >
                        Réinitialiser
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        variant="text"
                        color="primary"
                        startIcon={<CheckCircle />}
                        onClick={() => handleToggleEstPasse(etudiant)}
                        disabled={!etudiant.inscription}
                      >
                        Marquer passé
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {filteredEtudiants.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">Aucun étudiant trouvé</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredEtudiants.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        labelRowsPerPage="Lignes par page"
        sx={{ mt: 2 }}
      />

      {/* Dialog de confirmation de passation */}
      <Dialog open={passationDialogOpen} onClose={() => setPassationDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentTurnedIn color="primary" />
            Confirmer la passation
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 2 }}>
            Vous êtes sur le point d'effectuer la passation pour <strong>{selectedEtudiants.length}</strong> étudiant(s).
          </Typography>
          
          <Box sx={{ mt: 2, p: 2, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid #bbf7d0' }}>
            <Typography variant="subtitle2" fontWeight="bold" color="#166534">
              📋 Destination :
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
              <Chip 
                label={`Année: ${allAnnees.find(a => String(a.annee_id || a._id) === formulairePassation.annee_destination)?.intitule || 'N/A'}`}
                size="small"
                variant="outlined"
                color="primary"
              />
              <Chip 
                label={`Filière: ${allFilieres.find(f => String(f.filiere_id || f._id) === formulairePassation.filiere_destination)?.intitule || 'N/A'}`}
                size="small"
                variant="outlined"
                color="secondary"
              />
              <Chip 
                label={`Niveau: ${niveauxDestination.find(n => String(n.niveau_id || n._id) === formulairePassation.niveau_destination)?.intitule || 'N/A'}`}
                size="small"
                variant="outlined"
                color="success"
              />
            </Stack>
          </Box>

          {modeForcé && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#fef3c7', borderRadius: 2, border: '1px solid #f59e0b' }}>
              <Typography variant="body2" color="#92400e">
                ⚠️ Mode forcé activé : les étudiants seront passés sans condition de note.
              </Typography>
            </Box>
          )}
          
          <Box sx={{ mt: 2, p: 2, bgcolor: '#fee2e2', borderRadius: 2, border: '1px solid #ef4444' }}>
            <Typography variant="body2" color="#991b1b">
              ⚠️ Cette action est irréversible.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPassationDialogOpen(false)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={confirmPassation}
            disabled={passationLoading}
            sx={{ bgcolor: modeForcé ? '#f59e0b' : '#10b981', '&:hover': { bgcolor: modeForcé ? '#d97706' : '#059669' } }}
          >
            {passationLoading ? <CircularProgress size={24} /> : 'Confirmer la passation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Passation;


